import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Footer from '../../components/feature/Footer';
import Badge from '../../components/base/Badge';
import { getOrganizationApi, fetchAllCourses } from '../../api/apiServices';
import { useAuth } from '../../context/useAuth';

const levelColorMap = {
  Beginner: 'teal',
  Intermediate: 'amber',
  Advanced: 'rose',
};

function OrgCourseCard({ course, accentColor }) {
  const courseId = course._id || course.id;
  const totalLessons = course.sections?.reduce((total, section) => {
    return total + (section.lectures?.length || 0);
  }, 0) || (course.lessons?.length || 0);

  return (
    <Link
      to={`/courses/${courseId}`}
      className="group bg-white rounded-2xl border border-slate-100 overflow-hidden hover:-translate-y-1 transition-all cursor-pointer block"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={course.thumbnail}
          alt={course.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3">
          <Badge label={course.level} variant={levelColorMap[course.level] || 'slate'} />
        </div>
        <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-lg flex items-center gap-1">
          <i className="ri-time-line" />{course.duration}
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-slate-400">{course.instructor || 'Unknown'}</span>
          <span className="text-xs text-slate-300">·</span>
          <span className="text-xs text-slate-400">
            {totalLessons} lesson{totalLessons !== 1 ? 's' : ''}
          </span>
        </div>

        <h3 className="text-base font-bold text-slate-900 leading-snug mb-1.5 line-clamp-2 transition-colors" style={{ '--tw-hover-color': accentColor }}>
          {course.title}
        </h3>
        <p className="text-sm text-slate-500 line-clamp-2 mb-3">{course.description}</p>

        <div className="flex items-center justify-between pt-3.5 border-t border-slate-100">
          <div>
            {!course.isPaid || !course.price || course.price === 0 ? (
              <span className="text-sm font-extrabold text-emerald-600">Free</span>
            ) : (
              <span className="text-sm font-extrabold text-slate-900">${course.price}</span>
            )}
          </div>
          <span className="text-xs font-semibold flex items-center gap-1 text-slate-800" style={{ color: accentColor }}>
            View Course <i className="ri-arrow-right-line" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function OrgCatalogPage() {
  const { slug } = useParams();
  const { isAuthenticated, user } = useAuth();
  const [org, setOrg] = useState(null);
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const orgResponse = await getOrganizationApi(slug);
        const organization = orgResponse?.organization || orgResponse;
        if (!organization || (!organization._id && !organization.slug)) {
          throw new Error('Organization not found');
        }
        if (cancelled) return;
        setOrg(organization);

        const coursesResponse = await fetchAllCourses({ organizationId: organization._id });
        if (cancelled) return;
        if (coursesResponse.success && Array.isArray(coursesResponse.courses)) {
          setCourses(coursesResponse.courses.filter((c) => c.isPublished === true));
        }
      } catch (err) {
        console.error('Failed to load organization catalog:', err);
        if (!cancelled) setError("This organization's catalog could not be found.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const filteredCourses = useMemo(() => {
    if (!search.trim()) return courses;
    const term = search.toLowerCase().trim();
    return courses.filter(
      (c) =>
        c.title?.toLowerCase().includes(term) ||
        c.description?.toLowerCase().includes(term) ||
        c.instructor?.toLowerCase().includes(term)
    );
  }, [courses, search]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading catalog...</p>
        </div>
      </div>
    );
  }

  if (error || !org) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <i className="ri-building-2-line text-5xl text-slate-300 mb-4" />
          <p className="text-slate-700 font-semibold mb-1">Organization not found</p>
          <p className="text-slate-400 text-sm mb-6">{error || "We couldn't find that organization's catalog."}</p>
          <Link to="/courses" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold transition-colors">
            Browse all courses <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    );
  }

  const primaryColor = org.theme?.primaryColor || '#0d9488';
  const backgroundColor = org.theme?.backgroundColor || '#0f172a';
  const backgroundImageUrl = org.theme?.backgroundImageUrl || '';
  // backgroundColor always applies underneath -- it's what shows while the
  // image loads and behind the gradient overlay that keeps the hero text
  // readable over a busy photo. A theme with no image renders exactly as
  // it did before this field existed.
  const heroStyle = backgroundImageUrl
    ? {
        backgroundColor,
        backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.45), rgba(0,0,0,0.65)), url(${backgroundImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : { backgroundColor };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40" style={{ backgroundColor }}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to={`/o/${org.slug}`} className="flex items-center gap-2.5">
            {org.logoUrl ? (
              <img src={org.logoUrl} alt={org.name} className="h-9 w-9 rounded-lg object-cover" />
            ) : (
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: primaryColor }}
              >
                {org.name?.charAt(0)?.toUpperCase() || 'O'}
              </div>
            )}
            <span className="text-white font-bold tracking-wide">{org.name}</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/courses"
              className="text-xs font-semibold text-white/70 hover:text-white transition-colors hidden sm:inline-block"
            >
              Browse all of LearnHub <i className="ri-external-link-line ml-1" />
            </Link>
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                className="text-xs font-bold text-white bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-user-line mr-1" /> {user?.firstName || user?.userName || 'My Dashboard'}
              </Link>
            ) : (
              // Registering (or signing in) from here keeps the person tied
              // to this organization -- the `redirect` param sends them
              // straight back to this catalog page afterward instead of the
              // app's generic dashboard, unless they choose "Back to home"
              // on that page instead.
              <div className="flex items-center gap-2">
                <Link
                  to={`/login?redirect=${encodeURIComponent(`/o/${org.slug}`)}`}
                  className="text-xs font-semibold text-white/80 hover:text-white transition-colors whitespace-nowrap"
                >
                  Log in
                </Link>
                <Link
                  to={`/signup?redirect=${encodeURIComponent(`/o/${org.slug}`)}`}
                  className="text-xs font-bold text-white px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
                  style={{ backgroundColor: primaryColor }}
                >
                  Sign up
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      <div style={heroStyle} className="pb-14 pt-10">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-3xl font-bold text-white mb-2">{org.name}</h1>
          <p className="text-white/60 mb-6 max-w-2xl">
            Courses and training from {org.name}, powered by LearnHub.
          </p>
          <div className="relative max-w-xl">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
              <i className="ri-search-line" />
            </div>
            <input
              type="text"
              placeholder="Search this catalog..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white/95 border border-white/10 rounded-xl text-sm focus:outline-none"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <p className="text-sm text-slate-500 mb-6">
          Showing {filteredCourses.length} course{filteredCourses.length !== 1 ? 's' : ''}
        </p>

        {filteredCourses.length === 0 ? (
          <div className="text-center py-20">
            <i className="ri-book-3-line text-6xl text-slate-300 mb-4" />
            <p className="text-slate-600 font-medium">No published courses yet</p>
            <p className="text-slate-400 text-sm mt-1">Check back soon for new content from {org.name}.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredCourses.map((course) => (
              <OrgCourseCard key={course._id} course={course} accentColor={primaryColor} />
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
